import React, { createContext, useContext, ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { DeprecationPlan } from '../types'
import { useDeprecationPlans } from '../queries/useDeprecationPlans'

interface DeprecationPlanContextValue {
  deprecationPlans: DeprecationPlan[]
  deprecationPlanMap: Map<number, DeprecationPlan>
  isLoading: boolean
  getDeprecationPlanForAccount: (params: { balanceSheetRowId?: number }) => DeprecationPlan | undefined
}

const DeprecationPlanContext = createContext<DeprecationPlanContextValue | null>(null)

interface DeprecationPlanProviderProps {
  children: ReactNode
}

/**
 * Provider for deprecation plan data - fetches once and shares via context
 * Loads all deprecation plans for the selected budgeting scenario
 * Use this to avoid creating multiple React Query observers
 */
export const DeprecationPlanProvider: React.FC<DeprecationPlanProviderProps> = ({ children }) => {
  const { budgetingScenario } = useSelector(filtersSelector)

  const { data: deprecationPlans = [], isLoading } = useDeprecationPlans({
    budgetingScenarioId: budgetingScenario?.id
  })

  // Create deprecation plan lookup map for O(1) access by balanceSheetRowId
  const deprecationPlanMap = useMemo(() => {
    const map = new Map<number, DeprecationPlan>()

    deprecationPlans.forEach(plan => {
      if (plan.balanceSheetRowId) {
        map.set(plan.balanceSheetRowId, plan)
      }
    })

    return map
  }, [deprecationPlans])

  // Helper function to get deprecation plan by balanceSheetRowId
  const getDeprecationPlanForAccount = useMemo(
    () =>
      (params: { balanceSheetRowId?: number }): DeprecationPlan | undefined => {
        const { balanceSheetRowId } = params
        if (!balanceSheetRowId) return undefined
        return deprecationPlanMap.get(balanceSheetRowId)
      },
    [deprecationPlanMap]
  )

  return (
    <DeprecationPlanContext.Provider
      value={{
        deprecationPlans,
        deprecationPlanMap,
        isLoading,
        getDeprecationPlanForAccount
      }}
    >
      {children}
    </DeprecationPlanContext.Provider>
  )
}

/**
 * Hook to access deprecation plan data from context
 * Must be used within DeprecationPlanProvider
 */
export const useDeprecationPlanContext = (): DeprecationPlanContextValue => {
  const context = useContext(DeprecationPlanContext)

  if (!context) {
    throw new Error('useDeprecationPlanContext must be used within DeprecationPlanProvider')
  }

  return context
}
